import { Button, Paper, PaperBorderType } from '@roc/ui';
import { Grid, makeStyles } from '@material-ui/core';
import { useDrawsStore } from '@roc/feature-draws';
import { observer } from 'mobx-react';
import { ApproveSOW } from './actions/approveSOW';
import { StartInspectionProcess } from './actions/startInspectionProcess';
import { ApproveInspection } from './actions/approveInspection';
import { RequestBankInformation } from './actions/requestBankInformation';
import { ApproveWire } from './actions/approveWire';
import { ResolveDraw } from './actions/resolveDraw';
import { RejectDraw } from './actions/rejectDraw';
import { SetDrawStage } from './actions/setDrawStage';
import { LinkTransactionToDraw } from './actions/linkTransactionToDraw';
import { MoveSnapDrawPhotos } from './actions/moveSnapDrawPhotos';
import { DocumentName, insertIf, LoanProcess } from '@roc/feature-utils';
import { DRAW_INSURANCE_TYPE, DRAW_REGULAR_TYPE, DRAWS_REVISION_STAGE, SELF_INSPECTION } from 'libs/feature-draws/src/draws/stores/drawActionsStore';
import { SendWire } from './actions/sendWire';
import { RejectRevision } from './actions/rejectRevision';
import { ApproveRevision } from './actions/approveRevision';
import { useDocumentStore } from '@roc/feature-documents';
import { SOFT_COST_DRAW_TYPE } from 'libs/feature-draws/src/draws/utils/constants';
import { TaskV2Type } from '@roc/feature-general-tasks';

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    padding: '0 16px 16px 16px'
  },
  button: {
    height: '30px',
    padding: '4px 8px',
    border: '1px solid #285391',
    color: '#285391',
    borderRadius: '4px',
    textTransform: 'none',
  },
}));

export const DrawActionButtons = observer(({ loanId, drawId }) => {

  const classes = useStyles();
  const { drawStore: { drawActionsStore }, drawStore } = useDrawsStore();
  const { drawDTO: { draw } } = drawStore;
  const { documentStore } = useDocumentStore();

  const getbuttons = () => {
    if (!draw?.rejected) {
      if (draw?.stage === DRAWS_REVISION_STAGE) {
        return [
          {
            label: 'Approve Revision',
            onClick: () => {
              drawActionsStore.setIsApproveRevisionDialogOpen(true);
            }
          },
          {
            label: 'Reject Revision',
            onClick: () => {
              drawActionsStore.setIsRejectRevisionDialogOpen(true);
            }
          },
        ]
      }
      else if (draw?.revision === false && draw.drawType == DRAW_REGULAR_TYPE) {
        return [{
          label: 'Approve SOW',
          onClick: () => {
            drawActionsStore.setIsApproveSOWsDialogOpen(true);
          }
        }, ...insertIf(draw?.drawSubtype !== SOFT_COST_DRAW_TYPE, [{
          label: 'Start Inspection Process',
          onClick: () => {
            drawActionsStore.setIsStartInspectionProcessDialogOpen(true);
          }
        }]),
        ...insertIf(draw.inspectionCompany !== SELF_INSPECTION, [{
          label: 'Approve Inspection',
          onClick: () => {
            drawActionsStore.setIsApproveInspectionDialogOpen(true);
          }
        }]),
        {
          label: 'Request Bank Information',
          onClick: () => {
            drawActionsStore.setIsRequestBankInformationDialogOpen(true);
          }
        },
        {
          label: 'Send Wire',
          onClick: () => {
            drawActionsStore.setIsSendWireDialogOpen(true);
          }
        },
        ...insertIf(drawStore?.globalStore?.userFeatures?.approveWireButton, [{
          label: 'Approve Wire',
          onClick: () => {
            drawActionsStore.setIsApproveWireDialogOpen(true);
          }
        }]),
        {
          label: 'Mark Wire Sent/Draw Resolved',
          onClick: () => {
            drawActionsStore.setIsResolveDrawDialogOpen(true);
          }
        }, {
          label: 'Reject Draw',
          onClick: () => {
            drawActionsStore.setIsRejectDrawDialogOpen(true);
          }
        }, {
          label: 'Set Draw Stage',
          onClick: () => {
            drawActionsStore.setIsSetDrawStageDialogOpen(true);
          }
        }, {
          label: 'Link Transaction to Draw',
          onClick: () => {
            drawActionsStore.openLinkTransactionToDrawDialog(loanId, draw?.drawType);
          }
        }, {
          label: 'Move Snap Draws Photos',
          onClick: () => {
            drawActionsStore.openMoveSnapDrawsPhotosDialog(drawId);
          }
        },
        ]
      }
      else if (draw?.drawType == DRAW_INSURANCE_TYPE) {
        return [{
          label: 'Link Transaction to Draw',
          onClick: () => {
            drawActionsStore.openLinkTransactionToDrawDialog(loanId, draw?.drawType);
          }
        },
        {
          label: 'Mark Wire Sent/Draw Resolved',
          onClick: () => {
            drawActionsStore.setIsResolveDrawDialogOpen(true);
          }
        }, {
          label: 'Reject Draw',
          onClick: () => {
            drawActionsStore.setIsRejectDrawDialogOpen(true);
          }
        }]
      }

      return [];
    }
  }

  const afterActionExecutedHandler = () => {
    drawStore.fetchDrawDTOById(drawId);
    documentStore.loanTodosStore.fetchTodos(loanId, LoanProcess.DRAWS, TaskV2Type.DRAW_ID, drawId);
  }

  const todos = documentStore.loanTodosStore.todosByLoanProcess[LoanProcess.DRAWS] ?? [];
  const hasRevisedSowForm = todos.some((todo) => todo?.document?.taskName === DocumentName.REVISED_SOW_FORM);
  const borrowers = documentStore?.loanStore?.loanDetails?.borrowers ?? [];
  const isSnapDrawEligible = borrowers.every(b => b?.isEligibleForSnapDraws === true);

  return (
    <>
      {!draw.rejected &&
        <Paper title='Actions' borderType={PaperBorderType.SOLID_BORDER}>
          <Grid container className={classes.buttonsContainer} direction='row' spacing={1}>
            {getbuttons().map(({ label, onClick }) => (
              <Grid item key={label.replace(/\s+/g, '-').toLowerCase()}>
                <Button
                  testId={label.replace(/\s+/g, '-').toLowerCase()}
                  variant='outlined'
                  className={classes.button}
                  onClick={onClick}
                >
                  {label}
                </Button>
              </Grid>
            ))
            }
          </Grid>
          <ApproveSOW loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <StartInspectionProcess loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} isSnapDrawEligible={isSnapDrawEligible} />
          <ApproveInspection loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <RequestBankInformation loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <SendWire loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <ApproveWire loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <ResolveDraw loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <RejectDraw loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <SetDrawStage loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <LinkTransactionToDraw loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <MoveSnapDrawPhotos loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
          <ApproveRevision loanId={loanId} draw={draw} hasRevisedSowForm={hasRevisedSowForm} afterActionExecuted={afterActionExecutedHandler} />
          <RejectRevision loanId={loanId} drawId={drawId} afterActionExecuted={afterActionExecutedHandler} />
        </Paper>
      }
    </>
  );
});
